import { generatePassword } from "../utils/generate-temporary-password.ts";
import React, { useMemo, useState } from "react";
import { useForm } from "@tanstack/react-form";

import { Button, TextField } from "../../../ui/index.ts";
import { formatFormFieldErrors } from "../../auth/forms/form-errors.ts";
import { useItApprovalUpdateCcEmail } from "../hooks/use-it-approval-update-cc-email.ts";
import {
  itApprovalFormSchema,
  type ItApprovalFormValues,
} from "../schemas/it-approval-form.schema.ts";
import type { ItApprovalEmployeeRow } from "../schemas/it-approval-employee.schema.ts";
import {
  formatItApprovalGender,
  formatItApprovalJoiningDate,
  formatItApprovalNamePart,
  formatItApprovalPhone,
} from "../utils/format-it-approval-display.ts";
import { ApiError } from "../../../../utils/api.ts";
import { getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { GET_EMPLOYEE_PROFILE_BY_EMPID } from "../../../../../config/Endpoints.js";
import { readOnlyFieldValue } from "../../../../utils/read-only-field-value.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";

import styles from "./it-approval-form.module.css";
import { Card } from "../../../shared/card/card.tsx";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";

export type ItApprovalFormProps = {
  employee: ItApprovalEmployeeRow;
  onCancel: () => void;
  onSuccess: () => void;
};

const field = (name: keyof ItApprovalFormValues) => name;

export function ItApprovalForm({ employee, onCancel, onSuccess }: ItApprovalFormProps) {
  const isSqe = useSqeLabs() || employee.employeeType === "SQELABS";
  const { updateCcEmail, loading } = useItApprovalUpdateCcEmail(isSqe);

  const [dob, setDob] = useState<Date | null>(() => {
    // start with employee-provided dob when available; we'll refresh from API
    if (!employee?.dob) return null;
    const parsed = new Date(employee.dob);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  });

  const generatedPassword = useMemo(
    () => generatePassword(employee.firstName, dob),
    [employee.firstName, dob],
  );

  // fetch fresh employee profile (to ensure dob is authoritative) and update DOB
  React.useEffect(() => {
    let mounted = true;
    async function fetchProfile() {
      if (isSqe) return;
      try {
        const res = await getApi(`${GET_EMPLOYEE_PROFILE_BY_EMPID}/${employee.id}`, accessToken());
        if (!mounted) return;

        // response shapes vary; try common locations for dob
        const anyRes: any = res;
        const dobStr =
          anyRes?.data?.data?.dob ??
          (Array.isArray(anyRes?.data?.data) && anyRes.data.data[0]?.dob) ??
          anyRes?.data?.dob ??
          employee.dob ??
          null;

        if (dobStr) {
          const parsed = new Date(dobStr);
          if (!Number.isNaN(parsed.getTime())) {
            setDob(parsed);
          }
        }
      } catch (e) {
        // ignore — keep existing dob if any
      }
    }

    void fetchProfile();

    return () => {
      mounted = false;
    };
  }, [employee.id, isSqe]);

  const initialCcEmail =
    typeof employee.ccEmail === "string" ? employee.ccEmail.trim() : "";

  const form = useForm({
    defaultValues: { ccEmail: initialCcEmail } satisfies ItApprovalFormValues,
    validators: { onSubmit: itApprovalFormSchema as never },
    onSubmit: async ({ value }) => {
      try {
        const data = await updateCcEmail(employee.id, value.ccEmail);
        showSuccess(data?.message ?? "User Email has been successfully updated.");
        onSuccess();
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : "Failed to update company email.";
        showError(msg);
      }
    },
  });

  const busy = loading;

  const handleCopyPassword = async () => {
    if (!generatedPassword) return;

    try {
      await navigator.clipboard.writeText(generatedPassword);
      showSuccess("Password copied to clipboard.");
    } catch {
      showError("Unable to copy password. Please try again.");
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      noValidate
    >
      <div className={styles.readOnlyRow}>
        <Card className={styles.formCard}>
          <TextField
            id="it-approval-first-name"
            label="First Name"
            value={formatItApprovalNamePart(employee.firstName)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-middle-name"
            label="Middle Name"
            value={readOnlyFieldValue(employee.middleName, true, { optional: true })}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-last-name"
            label="Last Name"
            value={formatItApprovalNamePart(employee.lastName)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-personal-email"
            label="Personal Email Address"
            type="email"
            value={readOnlyFieldValue(employee.personalEmail, true)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-gender"
            label="Gender"
            value={formatItApprovalGender(employee.gender)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-joining-date"
            label="Date of Joining"
            value={formatItApprovalJoiningDate(employee)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-phone"
            label="Contact Number"
            value={formatItApprovalPhone(employee.phoneNumber)}
            readOnly
            disabled
          />
          <TextField
            id="it-approval-dob"
            label="Date of Birth"
            value={
              dob
                ? dob.toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : "N/A"
            }
            readOnly
            disabled
          />
          {!isSqe && <div className={styles.passwordRow}>
            <TextField
              id="it-approval-password"
              label="Password"
              value={generatedPassword}
              readOnly
            />
            <Button
              type="button"
              variant="muted-outline"
              disabled={!generatedPassword || busy}
              onClick={handleCopyPassword}
            >
              Copy
            </Button>
          </div>}
        </Card>
      </div>

      <div className={styles.actionSection}>
        <div className={styles.inputWrapper}>
          <form.Field name={field("ccEmail")}>
            {(f) => (
              <TextField
                id="it-approval-cc-email"
                label={isSqe ? "SqeLabs Email" : "Caelius Email"}
                type="email"
                required
                maxLength={100}
                value={f.state.value}
                onChange={(e) => f.handleChange(e.target.value)}
                onBlur={f.handleBlur}
                error={formatFormFieldErrors(f.state.meta.errors)}
                disabled={busy}
                placeholder="Enter Employee Email"
              />
            )}
          </form.Field>
        </div>

        <div className={styles.actions}>
          <Button type="button" variant="muted-outline" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Submitting…" : "Submit"}
          </Button>
        </div>
      </div>
    </form>
  );
}
